/** "Drop these three, and here is why."
 *
 *  Every task mutation in this app was one task, one call, so writing off a client
 *  that had gone quiet meant cancelling its work a row at a time and typing the same
 *  reason into each one.
 *
 *  Shaped on `ChasePanel`, which is the one thing here that already picks several
 *  people and reports per item what happened to each. The part worth copying is not
 *  the checkbox - it is that report: a bulk action partly fails, and saying "done"
 *  over the top of two refusals is how somebody comes to believe work was dropped
 *  when it was not.
 *
 *  Cancelling is the only bulk action offered, deliberately. `TaskCards` argues that
 *  "a one-tap Approve buried in a list is how work gets waved through", and a bulk
 *  approve is exactly that; a bulk complete would fire an approval request per task
 *  at whoever has to look at them. Dropping has neither problem.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { api, type DropResult } from '@/api/client'
import { ErrorNote } from '@/components/ui'
import type { Selection } from './Selection'

/** Matches `ReasonIn` and `TaskService._require_reason`. Said here so nobody meets
 *  the rule as a 422 after typing three words. */
const MIN_REASON = 10

export function DropSelected({ selection, channelName }: {
  selection: Selection
  /** Whatever the firm calls its bot, so the warning names the real thing. */
  channelName: string
}) {
  const qc = useQueryClient()
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  const [done, setDone] = useState<DropResult | null>(null)
  const [err, setErr] = useState<unknown>(null)

  const drop = useMutation({
    mutationFn: () => api.post<DropResult>('/tasks/bulk/cancel', {
      task_ids: selection.ids, reason,
    }),
    onSuccess: (out) => {
      setErr(null)
      setDone(out)
      setAsking(false)
      setReason('')
      // Only what actually went is cleared. Leaving the refused ones ticked is the
      // point: they are what somebody still has to decide about, and clearing them
      // would hide the problem behind a tidy screen.
      out.cancelled.forEach((c) => selection.toggle(c.task_id))
      ;['tasks', 'day', 'queue', 'queue-count'].forEach(
        (k) => qc.invalidateQueries({ queryKey: [k] }))
    },
    onError: setErr,
  })

  if (done) {
    return (
      <Bar>
        <div className="flex-1 min-w-0 space-y-1">
          <b className="text-[13px]">
            {done.cancelled.length} dropped
            {done.skipped.length > 0 && `, ${done.skipped.length} not`}
          </b>
          {/* Named, not counted. "2 could not be dropped" leaves somebody hunting
              a list of twenty for which two. */}
          {done.skipped.length > 0 && (
            <ul className="text-[12.3px] text-ink-2">
              {done.skipped.map((s) => (
                <li key={s.task_id}>
                  <span className="mono text-ink-3">
                    {s.ref ? `#${s.ref}` : s.task_id}
                  </span>{' '}— {s.why}
                </li>))}
            </ul>
          )}
          {done.told.length > 0 && (
            <p className="text-[11.8px] text-ink-3">
              {channelName} sent to {done.told.map((t) => t.name).join(', ')}.
            </p>
          )}
        </div>
        <button className="btn" onClick={() => setDone(null)}>Close</button>
      </Bar>
    )
  }

  if (selection.count === 0) return null

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      <Bar>
        <span className="text-[12.5px] text-ink-2">
          <b className="mono">{selection.count}</b> selected
        </span>
        <span className="flex-1" />
        {asking ? (
          <>
            <input autoFocus value={reason} maxLength={1000}
                   onChange={(e) => setReason(e.target.value)}
                   placeholder="Why? Sent to them word for word."
                   className="field w-full sm:w-80" />
            <span className="mono text-[11px] text-ink-3">
              {reason.trim().length}/{MIN_REASON}
            </span>
            <button className="btn btn-bad"
                    disabled={reason.trim().length < MIN_REASON || drop.isPending}
                    onClick={() => drop.mutate()}>
              {drop.isPending ? 'Dropping…' : `Drop ${selection.count}`}
            </button>
            <button className="btn" onClick={() => { setAsking(false); setReason('') }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button className="btn" onClick={selection.clear}>Clear</button>
            <button className="btn btn-bad" onClick={() => setAsking(true)}>
              Drop {selection.count}…
            </button>
          </>
        )}
      </Bar>
    </>
  )
}

/** Sticky, because a selection made at the top of forty rows is acted on from
 *  wherever the reading stopped. */
function Bar({ children }: { children: React.ReactNode }) {
  return (
    <div className="sticky bottom-0 z-10 mt-3 card px-4 py-2.5 shadow-lg
                    flex flex-wrap items-center gap-2">
      {children}
    </div>
  )
}
